<script lang="ts">
    import {
        TEST_ID,
        EVENTS,
    } from '@amp/web-app-components/src/components/lockups/EditorialCard/constants';
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';
    import type { Artwork as IArtwork } from '@amp/web-app-components/src/components/Artwork/types';
    import { createEventDispatcher } from 'svelte';

    let titleHeight: number;

    const DEFAULT_TITLE_HEIGHT = 22;

    /**
     * @name EditorialCard
     * @description
     * This implements the standard lockup - without callout/cover art overlay
     *
     * Future TBD:
     * - Callout with cover art
     *
     * Design:
     * https://pd-hi.apple.com/viewvc/Common/Modules/macOS/Podcasts/Lockups/Showcase%20Lockup.png?revision=56346&pathrev=57299
     *
     *
     **/

    /**
     * eyebrow text above image.
     * @type {string}
     */
    export let eyebrowText: string;
    /**
     * title of editorial card.
     * @type {string}
     */
    export let title: string;
    /**
     * accessibility label for editorial card.
     * @type {string}
     */
    export let ariaLabel: string | null = null;
    /**
     * subtitle of editorial card.
     * @type {string}
     */
    export let subtitle: string;
    /**
     * description of editorial card.
     * @type {string}
     */
    export let description: string | undefined | null;
    /**
     * Artwork object.
     * @type {{ template: string, width: number, height: number, backgroundColor: string }} Artwork
     */
    export let artwork: IArtwork;
    /**
     * coverArtwork object.
     * @type {{ template: string, width: number, height: number, backgroundColor: string }} Artwork
     */
    export let coverArtwork: IArtwork | undefined | null;
    /**
     * should coverArtwork be rendered in video dimensions
     * @type {boolean}
     */
    export let isVideo: boolean = false;
    /**
     * show standalone play button if no description or cover art
     * @type {boolean}
     */
    export let hasStandaloneAction: boolean = false;

    /**
     * Adds an additonal anchor tag to EditorialCard at the same level to ensure
     * there are no nested clickables (links, buttons etc.),
     * This is useful when the EditorialCard must handle 2 different clickables.
     * @example
     * ``html
     *   <EditorialCard linkAction={ url: 'https://apple.com/foo', isExternal: true }>
     *      <svelte:fragment slot="play-button">
     *           <a href="https://apple.com/bar">Click Me</a>
     *      </svelte:fragment>
     *   </EditorialCard>
     *
     *   <!-- Returns -->
     *
     *   <a href="https://apple.com/foo">foobar</a>
     *   <div class="editorial-card__description">
     *       ...
     *   </div>
     *   <div class="editorial-card__artwork">
     *       ...
     *       <a href="https://apple.com/bar">Click Me</a>
     *   </div>
     * ``
     *
     * @event click - triggered when user clicks on editorial card
     * @type {{ url: string, isExternal: boolean|null }} clickable link
     */
    export let linkAction: {
        url: string;
        isExternal?: boolean | null;
    } = null;

    // If editorial card description includes a subtitle,
    // Allow editorial card title to wrap two lines to accommodate
    // long strings of metadata
    export let multilineTitle: boolean = false;

    const dispatch = createEventDispatcher();

    const handleLinkClick = (event: MouseEvent): void => {
        dispatch(EVENTS.linkClick, { event });
    };

    // Whether to render subtitle as artwork description
    $: displaySubtitleAsArtworkDescription =
        multilineTitle && titleHeight > DEFAULT_TITLE_HEIGHT;
    $: showSubtitle = !!subtitle && !displaySubtitleAsArtworkDescription;
    $: artworkDescription =
        (displaySubtitleAsArtworkDescription && subtitle) || description;

    $: linkProps = linkAction?.isExternal
        ? {
              target: '_blank',
              rel: 'noopener noreferer',
          }
        : {};
</script>

<div class="editorial-card" aria-label={ariaLabel ?? title}>
    {#if linkAction}
        <a
            class="editorial-card__link"
            data-testid={TEST_ID.LINK}
            href={linkAction.url ?? '#'}
            on:click={handleLinkClick}
            {...linkProps}
        >
            {title}
        </a>
    {/if}
    <div
        class="editorial-card__description"
        class:editorial-card__description--has-subtitle={subtitle}
    >
        {#if eyebrowText}
            <p data-testid={TEST_ID.EYEBROW} class="editorial-card__eyebrow">
                {eyebrowText}
            </p>
        {/if}
        <h2
            data-testid={TEST_ID.TITLE}
            class="editorial-card__title"
            class:multi-line-clamp={multilineTitle}
            dir="auto"
            bind:clientHeight={titleHeight}
        >
            {title}
        </h2>
        {#if showSubtitle}
            <p
                data-testid={TEST_ID.SUB_TITLE}
                class="editorial-card__sub-title"
            >
                {subtitle}
            </p>
        {/if}
    </div>
    <div class="editorial-card__artwork">
        <Artwork
            alt={ariaLabel ?? title}
            {artwork}
            profile="editorial-card"
            imageSettings={{ forceCropCode: true }}
        />
        {#if artworkDescription || coverArtwork}
            <div class="editorial-card__artwork-description">
                {#if artworkDescription}
                    <div
                        data-testid={TEST_ID.DESCRIPTION}
                        class="editorial-card__artwork-description-text"
                    >
                        {artworkDescription}
                    </div>
                {/if}
                {#if coverArtwork}
                    <div
                        data-testid={TEST_ID.COVER_ARTWORK}
                        class="editorial-card__artwork-cover-artwork"
                    >
                        <Artwork
                            artwork={coverArtwork}
                            noShelfChevronAnchor={true}
                            profile={isVideo
                                ? 'editorial-card-video-art'
                                : 'editorial-card-cover-artwork'}
                        />
                        <div
                            data-testid="play-button"
                            class="editorial-card__play-button"
                        >
                            <slot name="play-button" />
                        </div>
                    </div>
                {:else}
                    <div
                        data-testid="play-button"
                        class="editorial-card__play-button"
                    >
                        <slot name="play-button" />
                    </div>
                {/if}
            </div>
        {/if}
        {#if hasStandaloneAction}
            <div data-testid="play-button" class="editorial-card__play-button">
                <slot name="play-button" />
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @use 'amp/stylekit/core/mixins/hover-style' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/aspect-ratio' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'ac-sasskit/core/locale' as *;

    .editorial-card {
        --linkHoverTextDecoration: none;
        --playButtonCircleHoverColor: var(--keyColor);
        --iconCircleFillBGOverride: transparent; // Transparent to allow play-button material to be visible
        position: relative;
    }

    .editorial-card__link {
        position: absolute;
        display: block;
        width: 100%;
        height: 100%;
        color: transparent;
        z-index: calc(var(--z-default) + 1);
    }

    .editorial-card__description {
        min-height: 60px;
        margin-bottom: 15px;

        // Adjust min-height to compensate for Thai leading en_US *1.4
        @include locale(th) {
            min-height: 84px;
        }
    }

    .editorial-card__eyebrow {
        margin: 1px 0 2px;
        color: var(--systemSecondary);
        font: var(--subhead-emphasized);

        @include line-clamp(1);
    }

    .editorial-card__title {
        margin: 0;
        padding: 0;
        color: var(--systemPrimary);
        text-align: match-parent;
        font: var(--title-2);

        @include line-clamp(2);
    }

    .editorial-card__description--has-subtitle .editorial-card__title {
        @include line-clamp(1);

        &.multi-line-clamp {
            @include line-clamp(2);
        }
    }

    .editorial-card__sub-title {
        color: var(--systemSecondary);
        font: var(--title-2);

        @include line-clamp(1);
    }

    .editorial-card__artwork {
        border-radius: var(
            --global-border-radius-large,
            #{$global-border-radius-large}
        );
        position: relative;

        &::after {
            @include content-container-hover-style;

            // To ensure there is no nested clickables (links, buttons etc.), EditorialCard has been rewritten it is at the same level
            // as, rather than nested in, the linkAction anchor tag that facilitates FlowActions. Since EditorialCard and its <a> are now siblings,
            // the general sibling combinator (~) is used to ensure that .editorial-card__artwork, which is now siblings to <a>, shows the proper hover state.
            .editorial-card:is(:hover, :active, :focus) &,
            :global(a:focus) ~ & {
                @include content-container-hover-active-style;
            }
        }

        &:focus-within {
            &::after {
                @include content-container-hover-active-style;
            }

            .editorial-card__play-button {
                opacity: 1;
            }
        }
    }

    .editorial-card__artwork-description {
        display: flex;
        align-items: flex-end;
        margin-top: -100px;
        width: 100%;
        bottom: 0;
        border-radius: inherit;
        white-space: normal;
        padding: 24px $spacerA $spacerA;
        min-height: 100px;
        justify-content: space-between;
        z-index: calc(var(--z-default) + 1);

        &::before {
            content: '';
            background: linear-gradient(transparent 0, rgba(0, 0, 0, 0.4));
            border-radius: inherit;

            @include absolute-center;
        }
    }

    .editorial-card__artwork-description-text {
        top: 4px;
        max-width: 85%;
        color: white;
        flex: 1;
        pointer-events: none;
        padding-inline-end: 20px;
        font: var(--callout);
        z-index: calc(var(--z-default) + 1);

        @include line-clamp(3);
    }

    .editorial-card__artwork-cover-artwork {
        margin-inline-start: auto;
        border-radius: 4px;
        position: relative;

        @include aspect-ratio($height: 60px);

        &::after {
            @include content-container-hover-style;

            :global(a:hover) &,
            :global(a:focus) &,
            :global(a:focus-within) & {
                @include content-container-hover-active-style;
            }
        }
    }

    .editorial-card__play-button {
        position: absolute;
        bottom: 15px;
        opacity: 0;
        inset-inline-end: 15px;
        transition: var(--global-transition);

        .editorial-card__artwork-cover-artwork & {
            display: flex;
            justify-content: center;
            align-items: center;
            pointer-events: none;

            @include absolute-center($z-offset: 1);
        }

        .editorial-card:focus-within &,
        .editorial-card:hover & {
            opacity: 1;
            z-index: calc(var(--z-default) + 1);
        }
    }
</style>
